import AddressTypes from 'common/Forms/AddressTypes';
import { ConcatAddressLine } from 'common/Addresses/AddressFormatters/Shared/AddressFormatHelper';

const splitByFirstComma = (value) => {
  if (typeof value !== 'string') {
    return ['', ''];
  }

  const commaIndex = value.indexOf(',');
  if (commaIndex === -1) {
    return [value.trim(), ''];
  }

  return [
    value.slice(0, commaIndex).trim(),
    value.slice(commaIndex + 1).trim()
  ];
};

function GetFormFieldPathValueMap(addressPath, wellFormedAddress) {
  const streetLine1 = wellFormedAddress?.addressLine1 ?? wellFormedAddress?.line1;
  const streetLine2 = wellFormedAddress?.addressLine2 ?? wellFormedAddress?.line2;

  const fieldPathValueDictionary = [
    {
      key: `${ addressPath }.line1`,
      value: ConcatAddressLine([streetLine1, streetLine2])
    },
    {
      key: `${ addressPath }.line2`,
      value: ''
    },
    { key: `${ addressPath }.line3`, value: wellFormedAddress?.addressLine3 ?? wellFormedAddress?.line3 },
    { key: `${ addressPath }.line4`, value: wellFormedAddress?.addressLine4 ?? wellFormedAddress?.line4 },
    { key: `${ addressPath }.postalCode`, value: wellFormedAddress?.addressPostalCode ?? wellFormedAddress?.postalCode },
    { key: `${ addressPath }.country`, value: wellFormedAddress?.addressCountry ?? wellFormedAddress?.country }
  ];

  return fieldPathValueDictionary;
}

function GetControllingPersonFieldPathValueMap(addressPath, wellFormedAddress) {
  const fieldPathValueDictionary = [
    { key: `${ addressPath }.line1`, value: wellFormedAddress?.addressLine1 ?? wellFormedAddress?.line1 },
    { key: `${ addressPath }.line2`, value: wellFormedAddress?.addressLine2 ?? wellFormedAddress?.line2 },
    { key: `${ addressPath }.line3`, value: wellFormedAddress?.addressLine3 ?? wellFormedAddress?.line3 },
    { key: `${ addressPath }.line4`, value: wellFormedAddress?.addressLine4 ?? wellFormedAddress?.line4 },
    { key: `${ addressPath }.postalCode`, value: wellFormedAddress?.addressPostalCode ?? wellFormedAddress?.postalCode },
    { key: `${ addressPath }.country`, value: wellFormedAddress?.addressCountry ?? wellFormedAddress?.country }
  ];

  return fieldPathValueDictionary;
}

function GetControllingPersonWellFormedAddress(formAddress) {
  const wellFormedAddress = {
    line1: formAddress?.line1 ?? '',
    line2: formAddress?.line2 ?? '',
    line3: formAddress?.line3 ?? '',
    line4: formAddress?.line4 ?? '',
    postalCode: formAddress?.postalCode ?? '',
    country: formAddress?.country ?? ''
  };

  return wellFormedAddress;
}

export function GetFieldPathValueMap(addressProps, wellFormedAddress) {
  switch (addressProps?.addressType) {
    case AddressTypes.Current:
      return GetFormFieldPathValueMap('customer.residenceAddress', wellFormedAddress);
    case AddressTypes.Mailing:
      return GetFormFieldPathValueMap('customer.mailingAddresses[0]', wellFormedAddress);
    case AddressTypes.HeadOffice:
      return GetFormFieldPathValueMap('customer.headOfficeAddress', wellFormedAddress);
    case AddressTypes.Partner:
      return GetFormFieldPathValueMap('customer.partnerAddress', wellFormedAddress);
    case AddressTypes.ControllingPersonPermanent:
      return GetControllingPersonFieldPathValueMap(`controllingPersons[${ addressProps?.controllingPersonIndex }].residenceAddress`, wellFormedAddress);
    case AddressTypes.ControllingPersonMailing:
      return GetControllingPersonFieldPathValueMap(`controllingPersons[${ addressProps?.controllingPersonIndex }].mailingAddress`, wellFormedAddress);
    default:
      break;
  }
}

function CreateWellFormedAddress(formAddress) {
  const [line1, line2FromStreet] = splitByFirstComma(formAddress?.line1);

  const wellFormedAddress = {
    line1,
    line2: line2FromStreet || formAddress?.line2 || '',
    line3: formAddress?.line3 || '',
    line4: formAddress?.line4 || '',
    postalCode: formAddress?.postalCode || '',
    country: formAddress?.country || ''
  };

  return wellFormedAddress;
}

export function GetWellFormedAddress(addressProps, addressValues) {
  switch (addressProps?.addressType) {
    case AddressTypes.Current:
      return CreateWellFormedAddress(addressValues?.customer?.residenceAddress);
    case AddressTypes.Mailing:
      return CreateWellFormedAddress(addressValues?.customer?.mailingAddresses?.[0]);
    case AddressTypes.HeadOffice:
      return CreateWellFormedAddress(addressValues?.customer?.headOfficeAddress);
    case AddressTypes.Partner:
      return CreateWellFormedAddress(addressValues?.customer?.partnerAddress);
    case AddressTypes.ControllingPersonPermanent:
      return GetControllingPersonWellFormedAddress(addressValues?.controllingPersons
            && addressValues.controllingPersons[addressProps?.controllingPersonIndex]?.residenceAddress);
    case AddressTypes.ControllingPersonMailing:
      return GetControllingPersonWellFormedAddress(addressValues?.controllingPersons
            && addressValues.controllingPersons[addressProps?.controllingPersonIndex]?.mailingAddress);
    default:
      break;
  }
}
